
import { computed, defineComponent, PropType, ref, watch } from 'vue'
import { CtProp } from '@/models/interfaces/content-type'
import LabelBox from '@/pages/Products/components/LabelBox.vue'
import ChevronRightIcon from '@/components/Icons/ChevronRightIcon.vue'
import { ResolvedCtProduct } from '@/pages/Products/ProductDetail/Index.vue'
import { QInput, useQuasar } from 'quasar'
import { useRouter } from 'vue-router'
import MinusCircleIcon from '@/components/Icons/MinusCircleIcon.vue'
import ResetIcon from '@/components/Icons/ResetIcon.vue'
import { debounce } from 'lodash'

export default defineComponent({
  name: 'ProductVariantCard',
  components: { ResetIcon, MinusCircleIcon, ChevronRightIcon, LabelBox },
  props: {
    editing: {
      type: Boolean,
      default: false,
    },
    productVariant: {
      type: Object as PropType<ResolvedCtProduct>,
      required: true,
    },
    productOptions: {
      type: Array as PropType<Array<CtProp>>,
      default: () => []
    },
    propValues: {
      type: Object as PropType<{ [key: CtProp['sys']['id']]: CtProp['value'][]}>
    }
  },
  emits: ['update:product-variant', 'unlink:product-variant', 'update:option-value'],
  setup (props, { emit }) {

    const $q = useQuasar()
    const router = useRouter()

    const productOptionsIds = computed(() => props.productOptions?.map(po => po?.sys?.id.toString()) ?? [])

    const productOptionNames = computed(() => props.productOptions?.reduce((prev, cur) => ({
      ...prev,
      [cur?.sys?.id]: cur?.name
    }), {}))

    const variantOptions = computed(() => {
      return productOptionsIds.value.filter(poId => productOptionNames.value[poId]).reduce((prev, cur) => ({
        ...prev,
        [cur]: props.productVariant?.components?.[0]?.props?.[cur] ?? ''
      }), {})
    })

    const onVariantClick = () => {
      if (props.editing) return
      $q.dialog({
        title: 'Go to variant page?',
        cancel: true,
        persistent: false
      }).onOk(() => (
        void router.push({ name: 'ProductDetailIndex', params: { productId: props.productVariant.sys.id } })
      ))
    }

    const propValueOptions = computed(() => Object.entries(props.propValues ?? {}).reduce((prev, [key, value]) => ({
      ...prev,
      [key]: Array.from(value).map(v => ({ label: v, value: v }))
    }), {}))

    const titleInput = ref<QInput>()
    const titleInputEl = computed(() => titleInput.value?.$el?.querySelector('input'))

    watch(() => [titleInputEl.value, props.productVariant.title], () => {
      if (!titleInputEl.value) return
      if (props.productVariant.title) {
        setInputWidth(props.productVariant.title)
      } else {
        if (titleInputEl.value.getAttribute('size') === '1') return
        titleInputEl.value.setAttribute('size', '1')
      }
    })

    const setInputWidth = (text: string | null) => {
      if (!titleInputEl.value) return
      const size = Math.max(
        (text ?? '').length,
        1,
        Math.ceil((titleInputEl.value.getAttribute('aria-label') ?? '').length / 2),
      )
      titleInputEl.value.setAttribute('size', size.toString())
    }

    const onTitleUpdate = debounce(
      ($event) => emit('update:product-variant', { ...props.productVariant, title: $event }),
      100,
      { leading: true }
    )

    return {
      titleInput,
      variantOptions,
      productOptionNames,
      propValueOptions,
      onVariantClick,
      onTitleUpdate
    }
  }
})
